import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import TexHubLogo from '@/components/brand/TexHubLogo';
import RegionSelector from '@/components/RegionSelector';
import { Lock, Loader2, CheckCircle2, Eye, EyeOff, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SetPassword = () => {
  const { isUSA } = useLocale();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError(isUSA ? 'Invalid or missing token' : 'Token inválido o faltante');
    }
  }, [token, isUSA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(isUSA ? 'Password must be at least 6 characters' : 'La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError(isUSA ? 'Passwords do not match' : 'Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        toast.success(isUSA ? 'Password set successfully!' : '¡Contraseña establecida!');
      } else {
        setError(data.detail || (isUSA ? 'Error setting password' : 'Error al establecer contraseña'));
      }
    } catch (error) {
      setError(isUSA ? 'Connection error' : 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <TexHubLogo size="default" />
            </Link>
            <div className="flex items-center gap-3">
              <RegionSelector variant="minimal" showLabel={false} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
              {isUSA ? 'Set your password' : 'Establece tu contraseña'}
            </h1>
            <p className="text-slate-600 mt-2">
              {isUSA 
                ? 'Create a password to access your buyer account' 
                : 'Crea una contraseña para acceder a tu cuenta de comprador'}
            </p>
          </div>

          <Card className="bg-white border-slate-200">
            <CardContent className="pt-6">
              {success ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {isUSA ? 'Password set successfully!' : '¡Contraseña establecida!'}
                  </h3>
                  <p className="text-slate-600 text-sm mb-6">
                    {isUSA 
                      ? 'You can now log in to view your orders and track your purchases.'
                      : 'Ya puedes iniciar sesión para ver tus pedidos y dar seguimiento a tus compras.'}
                  </p>
                  <Button 
                    onClick={() => navigate('/login')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {isUSA ? 'Go to login' : 'Ir a iniciar sesión'}
                  </Button>
                </div>
              ) : error && !token ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {isUSA ? 'Invalid link' : 'Enlace inválido'}
                  </h3>
                  <p className="text-slate-600 text-sm mb-6">
                    {isUSA 
                      ? 'This password setup link is invalid or has expired.'
                      : 'Este enlace para establecer contraseña es inválido o ha expirado.'}
                  </p>
                  <Link to="/login">
                    <Button variant="outline" className="w-full">
                      {isUSA ? 'Go to login' : 'Ir a iniciar sesión'}
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password">{isUSA ? 'New password' : 'Nueva contraseña'}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={isUSA ? 'At least 6 characters' : 'Mínimo 6 caracteres'}
                        className="h-12 pl-10 pr-10"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="set-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{isUSA ? 'Confirm password' : 'Confirmar contraseña'}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={isUSA ? 'Repeat your password' : 'Repite tu contraseña'}
                        className="h-12 pl-10"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        data-testid="set-password-confirm-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold bg-emerald-600 hover:bg-emerald-700"
                    disabled={loading}
                    data-testid="set-password-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {isUSA ? 'Setting password...' : 'Estableciendo...'}
                      </>
                    ) : (
                      isUSA ? 'Set password' : 'Establecer contraseña'
                    )}
                  </Button>

                  <div className="text-center pt-4 border-t">
                    <p className="text-sm text-slate-500">
                      {isUSA 
                        ? 'With your account you can:'
                        : 'Con tu cuenta podrás:'}
                    </p>
                    <ul className="text-sm text-slate-600 mt-2 space-y-1">
                      <li>✓ {isUSA ? 'View your order status' : 'Ver el estado de tus pedidos'}</li>
                      <li>✓ {isUSA ? 'Modify or cancel pending orders' : 'Modificar o cancelar pedidos pendientes'}</li>
                      <li>✓ {isUSA ? 'Access your purchase history' : 'Acceder a tu historial de compras'}</li>
                    </ul>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            {isUSA 
              ? 'Already have an account? ' 
              : '¿Ya tienes cuenta? '}
            <Link to="/login" className="font-medium hover:underline text-emerald-600">
              {isUSA ? 'Log in' : 'Inicia sesión'}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default SetPassword;
